/**
 * MacroEventsCalendarTab — Events & Actions
 *
 * Holds two record_type values in the same economic_events table:
 *   'event'  — external market events (FOMC, CPI, expiry…) — no completion
 *   'action' — user to-do items (Review, Trade, Rebalance…) — have checkbox + overdue state
 *
 * Views: Table (date-bucketed with Future/Past toggles) | Calendar (month grid)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select } from '../ui';
import IconRefresh from '../ui/IconRefresh';
import {
  getAllStrategy4Events,
  addStrategy4Event,
  updateStrategy4Event,
  deleteStrategy4Event,
  refreshStrategy4EventsFMP,
  uploadStrategy4EventsCSV,
  addAction,
  updateAction,
  deleteAction,
  completeCalendarItem,
  reopenCalendarItem,
} from '../../api/client';

// ── Constants ─────────────────────────────────────────────────────────────────

const EVENT_TYPE_OPTIONS = [
  'FOMC','RBI_MPC','US_CPI','US_NFP','IN_BUDGET','IN_ELECTION','NIFTY_MONTHLY_EXPIRY','OTHER',
];
const EVENT_TYPE_LABELS = {
  FOMC:'FOMC Rate Decision', RBI_MPC:'RBI MPC', US_CPI:'US CPI',
  US_NFP:'US NFP (Jobs)', IN_BUDGET:'Indian Budget', IN_ELECTION:'Indian Election',
  NIFTY_MONTHLY_EXPIRY:'NIFTY Monthly Expiry', OTHER:'Other',
};
const EVENT_TYPE_COLOR = {
  FOMC:'#f59e0b', RBI_MPC:'#6366f1', US_CPI:'#ef4444', US_NFP:'#f97316',
  IN_BUDGET:'#ec4899', IN_ELECTION:'#8b5cf6', NIFTY_MONTHLY_EXPIRY:'#64748b', OTHER:'#64748b',
};

const CATEGORY_OPTIONS = [
  'Monetary Policy','Economic Data','Fiscal Policy','Political',
  'Index Structure','Market Structure','Corporate','Misc',
];

const IMPACT_COLOR = { HIGH:'#ef4444', MEDIUM:'#f59e0b', LOW:'#94a3b8' };
const REGIONS = ['US','INDIA','GLOBAL','EU','CHINA','JAPAN'];

const ACTION_CATEGORIES = ['Review','Trade','Rebalance','Admin','Other'];
const ACTION_CAT_COLOR = {
  Review:'#6366f1', Trade:'#14b8a6', Rebalance:'#f59e0b', Admin:'#8b5cf6', Other:'#94a3b8',
};

const SOURCE_STYLE = {
  'auto-fmp':     { bg:'rgba(99,102,241,.10)',  c:'#6366f1' },
  'auto':         { bg:'var(--surface-3)',       c:'var(--ink-3)' },
  'manual':       { bg:'rgba(245,158,11,.10)',   c:'var(--amber-ink)' },
  'csv-upload':   { bg:'rgba(20,184,166,.10)',   c:'#14b8a6' },
  'strategy':     { bg:'rgba(139,92,246,.10)',   c:'#8b5cf6' },
  'ai-suggested': { bg:'rgba(245,158,11,.15)',   c:'var(--amber-ink)' },
};

const BLANK_EVENT = {
  event_date:'', event_type:'FOMC', title:'FOMC Rate Decision',
  impact:'HIGH', region:'US', category:'Monetary Policy', notes:'',
};
const BLANK_ACTION = {
  event_date:'', title:'', category:'Review', urgent:false, event_time:'', notes:'',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function monthLabel(ym) {
  return new Date(ym + '-15').toLocaleString('en', { month:'long', year:'numeric' });
}
function fmtDay(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleString('en', { weekday:'short', day:'numeric', month:'short' });
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function downloadTemplate() {
  const rows = [
    'event_date,event_type,title,impact,region,category,notes',
    '2026-06-17,FOMC,FOMC Rate Decision,HIGH,US,MONETARY_POLICY,Expected: hold',
  ];
  const blob = new Blob([rows.join('\n')], { type:'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'events_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ── Shared input style ────────────────────────────────────────────────────────

const inp = {
  boxSizing:'border-box', padding:'4px 7px', fontSize:11.5, color:'var(--ink)',
  background:'var(--ibg,var(--surface-2))', border:'.5px solid var(--hair-strong)',
  borderRadius:3, outline:'none', width:'100%',
};

// ── Tag pill ──────────────────────────────────────────────────────────────────

function Tag({ children, color }) {
  return (
    <span style={{
      display:'inline-block', fontSize:11.5, fontWeight:700,
      padding:'1px 6px', borderRadius:3, whiteSpace:'nowrap',
      color, background:`${color}1a`,
    }}>{children}</span>
  );
}

// ── Event add/edit form ───────────────────────────────────────────────────────

function EventForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...BLANK_EVENT, ...initial });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const lbl = { fontSize:11, color:'var(--ink-3)', marginBottom:3 };
  return (
    <div style={{ padding:'12px 16px', background:'var(--surface-2)',
      border:'.5px solid var(--hair)', borderRadius:6, marginBottom:12 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'var(--ink)', marginBottom:10, textTransform:'uppercase', letterSpacing:'.05em' }}>
        {initial?.id ? 'Edit Event' : 'New Event'}
      </div>
      {/* Rows 1+2 share one grid so Title (col4 row1) and Notes (col4 row2) are exactly the same width */}
      {/* Col layout: Date/Region | Type/Category | Impact (row2 only) | Title/Notes */}
      <div style={{ display:'grid', gridTemplateColumns:'130px 160px 80px 1fr', gap:8, marginBottom:10 }}>
        {/* Row 1 */}
        <div>
          <div style={lbl}>Date *</div>
          <input type="date" value={f.event_date} onChange={e=>set('event_date',e.target.value)} style={inp}/>
        </div>
        <div>
          <div style={lbl}>Type</div>
          <Select value={f.event_type} onChange={e=>{set('event_type',e.target.value);set('title',EVENT_TYPE_LABELS[e.target.value]||f.title);}}>
            {EVENT_TYPE_OPTIONS.map(v=><option key={v} value={v}>{EVENT_TYPE_LABELS[v]||v}</option>)}
          </Select>
        </div>
        <div/>{/* spacer — keeps Title in col 4 */}
        <div>
          <div style={lbl}>Title *</div>
          <input type="text" value={f.title} onChange={e=>set('title',e.target.value)} placeholder="Event title" style={inp}/>
        </div>
        {/* Row 2 */}
        <div>
          <div style={lbl}>Region</div>
          <Select value={f.region} onChange={e=>set('region',e.target.value)}>
            <option value="">—</option>
            {REGIONS.map(v=><option key={v} value={v}>{v}</option>)}
          </Select>
        </div>
        <div>
          <div style={lbl}>Category</div>
          <Select value={f.category} onChange={e=>set('category',e.target.value)}>
            <option value="">— Category —</option>
            {CATEGORY_OPTIONS.map(v=><option key={v} value={v}>{v}</option>)}
          </Select>
        </div>
        <div>
          <div style={lbl}>Impact</div>
          <Select value={f.impact} onChange={e=>set('impact',e.target.value)}>
            {['HIGH','MEDIUM','LOW'].map(v=><option key={v} value={v}>{v}</option>)}
          </Select>
        </div>
        <div>
          <div style={lbl}>Notes (optional)</div>
          <input type="text" value={f.notes} onChange={e=>set('notes',e.target.value)} placeholder="Expected outcome, context…" style={inp}/>
        </div>
      </div>
      {/* Row 3: buttons */}
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={()=>onSave(f)} disabled={saving||!f.event_date||!f.title}
          style={{ fontSize:11, padding:'5px 14px', borderRadius:4, cursor:'pointer', fontWeight:600,
            background:'var(--amber-ink)', color:'#fff', border:'none', opacity:saving?0.6:1 }}>
          {saving ? 'Saving…' : initial?.id ? 'Update Event' : 'Add Event'}
        </button>
        <button onClick={onCancel}
          style={{ fontSize:11, padding:'5px 12px', borderRadius:4, cursor:'pointer',
            background:'var(--surface-3)', color:'var(--ink-2)', border:'.5px solid var(--hair-strong)' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Action add/edit form ──────────────────────────────────────────────────────

function ActionForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...BLANK_ACTION, ...initial, urgent: !!(initial?.urgent) });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const lbl = { fontSize:11, color:'var(--ink-3)', marginBottom:3 };
  return (
    <div style={{ padding:'12px 16px', background:'var(--surface-2)',
      border:'.5px solid var(--hair)', borderRadius:6, marginBottom:12 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'var(--ink)', marginBottom:10, textTransform:'uppercase', letterSpacing:'.05em' }}>
        {initial?.id ? 'Edit Action' : 'New Action'}
      </div>
      {/* Row 1: Date | Time | Title (title takes remaining) */}
      <div style={{ display:'grid', gridTemplateColumns:'130px 110px 1fr', gap:8, marginBottom:8 }}>
        <div>
          <div style={lbl}>Due Date *</div>
          <input type="date" value={f.event_date} onChange={e=>set('event_date',e.target.value)} style={inp}/>
        </div>
        <div>
          <div style={lbl}>Time (optional)</div>
          <input type="time" value={f.event_time||''} onChange={e=>set('event_time',e.target.value)} style={inp}/>
        </div>
        <div>
          <div style={lbl}>Title *</div>
          <input type="text" value={f.title} onChange={e=>set('title',e.target.value)} placeholder="What to do…" style={inp}/>
        </div>
      </div>
      {/* Row 2: Urgent checkbox | Category | Notes (notes aligns under Title) */}
      <div style={{ display:'grid', gridTemplateColumns:'130px 110px 1fr', gap:8, alignItems:'center', marginBottom:10 }}>
        <div style={{ display:'flex', alignItems:'center', height:'100%', paddingTop:16 }}>
          <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:11.5, color:'var(--ink-2)', whiteSpace:'nowrap' }}>
            <input type="checkbox" checked={f.urgent} onChange={e=>set('urgent',e.target.checked)}
              style={{ accentColor:'var(--amber-ink)', width:13, height:13 }}/>
            Mark as urgent
          </label>
        </div>
        <div>
          <div style={lbl}>Category</div>
          <Select value={f.category} onChange={e=>set('category',e.target.value)}>
            {ACTION_CATEGORIES.map(v=><option key={v} value={v}>{v}</option>)}
          </Select>
        </div>
        <div>
          <div style={lbl}>Notes (optional)</div>
          <input type="text" value={f.notes||''} onChange={e=>set('notes',e.target.value)} placeholder="Context, details…" style={inp}/>
        </div>
      </div>
      {/* Row 3: buttons */}
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={()=>onSave({ ...f, urgent: f.urgent ? 1 : 0 })} disabled={saving||!f.event_date||!f.title}
          style={{ fontSize:11, padding:'5px 14px', borderRadius:4, cursor:'pointer', fontWeight:600,
            background:'var(--amber-ink)', color:'#fff', border:'none', opacity:saving?0.6:1 }}>
          {saving ? 'Saving…' : initial?.id ? 'Update Action' : 'Add Action'}
        </button>
        <button onClick={onCancel}
          style={{ fontSize:11, padding:'5px 12px', borderRadius:4, cursor:'pointer',
            background:'var(--surface-3)', color:'var(--ink-2)', border:'.5px solid var(--hair-strong)' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}


// ── Calendar grid ─────────────────────────────────────────────────────────────

function CalendarGrid({ allEvents, typeFilter, onComplete, onReopen, onEdit }) {
  const now = new Date();
  const [gridYM, setGridYM] = useState(
    `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`
  );
  const today = todayIso();

  const [yr, mo] = gridYM.split('-').map(Number);
  const firstDay  = new Date(yr, mo - 1, 1).getDay();
  const daysInMonth = new Date(yr, mo, 0).getDate();

  const byDay = {};
  allEvents.forEach(e => {
    const d = e.event_date;
    if (!d || !d.startsWith(gridYM)) return;
    (byDay[d] = byDay[d] || []).push(e);
  });

  const prevMonth = () => {
    const d = new Date(yr, mo - 2, 1);
    setGridYM(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const nextMonth = () => {
    const d = new Date(yr, mo, 1);
    setGridYM(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };

  const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  function Chip({ item }) {
    const isAction = item.record_type === 'action';
    const isDone   = isAction && !!item.completed_at;
    const cat      = isAction ? (item.category || 'Other') : (item.event_type || 'OTHER');
    const color    = isAction ? (ACTION_CAT_COLOR[cat] || '#94a3b8') : (EVENT_TYPE_COLOR[cat] || '#64748b');
    return (
      <div style={{
        display:'flex', alignItems:'center', gap:3, padding:'2px 5px', marginBottom:2,
        borderRadius:3, fontSize:10, background:`${color}18`,
        color: isDone ? 'var(--ink-3)' : 'var(--ink)',
        textDecoration: isDone ? 'line-through' : 'none',
        overflow:'hidden',
      }}>
        {isAction && (
          <span style={{ fontSize:11, lineHeight:1, color: isDone ? 'var(--ink-3)' : color,
            flexShrink:0, cursor:'pointer' }}
            onClick={ev => { ev.stopPropagation(); isDone ? onReopen(item.id) : onComplete(item.id); }}>
            {isDone ? '☑' : '☐'}
          </span>
        )}
        {!isAction && (
          <span style={{ width:6, height:6, borderRadius:'50%', background:color, flexShrink:0 }}/>
        )}
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          cursor:'pointer', flex:1 }}
          onClick={e => { e.stopPropagation(); onEdit(item); }}>
          {item.title}
        </span>
      </div>
    );
  }

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push({ blank: true, key: `b${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${gridYM}-${String(d).padStart(2,'0')}`;
    cells.push({ blank: false, key: dateStr, dateStr, d });
  }

  return (
    <div>
      {/* Month nav */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
        <button onClick={prevMonth}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:16,
            color:'var(--ink-2)', padding:'2px 6px' }}>‹</button>
        <span style={{ fontSize:13, fontWeight:700, color:'var(--ink)', minWidth:140, textAlign:'center' }}>
          {monthLabel(gridYM)}
        </span>
        <button onClick={nextMonth}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:16,
            color:'var(--ink-2)', padding:'2px 6px' }}>›</button>
        <button onClick={() => {
          const t = new Date();
          setGridYM(`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}`);
        }} className="pill" style={{ fontSize:11 }}>Today</button>
      </div>

      {/* Day-of-week headers */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:2, marginBottom:2 }}>
        {DOW.map(d => (
          <div key={d} style={{ fontSize:10, fontWeight:700, color:'var(--ink-3)', textAlign:'center',
            letterSpacing:'.04em', textTransform:'uppercase', padding:'4px 0' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Calendar cells — scrollable per cell, no expansion */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:2 }}>
        {cells.map(cell => {
          if (cell.blank) return <div key={cell.key} style={{ minHeight:90 }}/>;
          const { dateStr, d } = cell;
          const items = (byDay[dateStr] || []).filter(e =>
            typeFilter === 'event' ? (e.record_type === 'event' || !e.record_type) :
            typeFilter === 'action' ? e.record_type === 'action' : true
          );
          const isToday = dateStr === today;
          return (
            <div key={dateStr} style={{
              height:90, padding:'4px 5px', borderRadius:4,
              background: isToday ? 'rgba(245,158,11,.08)' : 'var(--surface)',
              border: isToday ? '1px solid var(--amber-hair,rgba(245,158,11,.4))' : '.5px solid var(--hair)',
              display:'flex', flexDirection:'column',
            }}>
              <div style={{ fontSize:10.5, fontWeight: isToday ? 700 : 400,
                color: isToday ? 'var(--amber-ink)' : 'var(--ink-3)',
                marginBottom:3, textAlign:'right', flexShrink:0 }}>
                {d}
              </div>
              <div style={{ flex:1, overflowY: items.length > 2 ? 'auto' : 'visible',
                minHeight:0 }}>
                {items.map(item => <Chip key={item.id} item={item}/>)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function MacroEventsCalendarTab() {
  const [events,       setEvents]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [typeFilter,   setTypeFilter]   = useState('all');    // 'all' | 'event' | 'action'
  const [impactFilter, setImpactFilter] = useState('');
  const [search,       setSearch]       = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [viewMode,     setViewMode]     = useState('table');  // 'table' | 'calendar'
  const [addType,      setAddType]      = useState(null);     // null | 'event' | 'action'
  const [modalItem,    setModalItem]    = useState(null);     // item being edited in modal
  const [savingId,     setSavingId]     = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [status,       setStatus]       = useState(null);
  const [showFuture,   setShowFuture]   = useState(true);
  const [showPast,     setShowPast]     = useState(false);
  const [refreshing,   setRefreshing]   = useState(false);
  const [lastFmpSync,  setLastFmpSync]  = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllStrategy4Events({
        impact: impactFilter || undefined,
      });
      setEvents(res.events || []);
    } catch (e) { setStatus({ ok:false, msg:`Load error: ${e.message}` }); }
    finally { setLoading(false); }
  }, [impactFilter]);

  useEffect(() => { load(); }, [load]);

  // ── Event handlers ──────────────────────────────────────────────────────────

  const handleAddEvent = async (form) => {
    setSavingId('new'); setStatus(null);
    try {
      await addStrategy4Event(form);
      setAddType(null);
      setStatus({ ok:true, msg:'✓ Event added' });
      await load();
    } catch (e) { setStatus({ ok:false, msg:`Error: ${e.message}` }); }
    finally { setSavingId(null); }
  };

  const handleAddAction = async (form) => {
    setSavingId('new'); setStatus(null);
    try {
      await addAction(form);
      setAddType(null);
      setStatus({ ok:true, msg:'✓ Action added' });
      await load();
    } catch (e) { setStatus({ ok:false, msg:`Error: ${e.message}` }); }
    finally { setSavingId(null); }
  };

  const handleModalSave = async (updated) => {
    const isAction = updated.record_type === 'action';
    setSavingId(updated.id); setStatus(null);
    try {
      if (isAction) {
        await updateAction(updated.id, {
          event_date: updated.event_date, title: updated.title,
          category: updated.category, urgent: updated.urgent,
          notes: updated.notes, event_time: updated.event_time,
        });
      } else {
        await updateStrategy4Event(updated.id, {
          title: updated.title, impact: updated.impact, notes: updated.notes,
          event_date: updated.event_date, region: updated.region, category: updated.category,
        });
      }
      setModalItem(null);
      setStatus({ ok:true, msg:'✓ Saved' });
      await load();
    } catch (e) { setStatus({ ok:false, msg:`Save error: ${e.message}` }); }
    finally { setSavingId(null); }
  };

  const handleDelete = async (id, isAction) => {
    if (!window.confirm(`Delete this ${isAction ? 'action' : 'event'}?`)) return;
    try {
      isAction ? await deleteAction(id) : await deleteStrategy4Event(id);
      await load();
    } catch (e) { setStatus({ ok:false, msg:`Delete error: ${e.message}` }); }
  };

  const handleComplete = async (id) => {
    setCompletingId(id);
    // Optimistic update
    setEvents(prev => prev.map(e => e.id === id
      ? { ...e, completed_at: new Date().toISOString() } : e));
    try {
      await completeCalendarItem(id);
    } catch (e) {
      setStatus({ ok:false, msg:`Complete failed: ${e.message}` });
      await load(); // revert on failure
    } finally { setCompletingId(null); }
  };

  const handleReopen = async (id) => {
    setCompletingId(id);
    setEvents(prev => prev.map(e => e.id === id ? { ...e, completed_at: null } : e));
    try {
      await reopenCalendarItem(id);
    } catch (e) {
      setStatus({ ok:false, msg:`Reopen failed: ${e.message}` });
      await load();
    } finally { setCompletingId(null); }
  };

  const handleEdit = (item) => {
    setAddType(null);   // close any open add-form first
    setModalItem(item);
  };

  const handleFMPRefresh = async () => {
    const fmpKey = localStorage.getItem('fmp_api_key') || null;
    if (!fmpKey && !window.confirm('No FMP key saved — use server env var FMP_API_KEY?')) return;
    setRefreshing(true); setStatus(null);
    try {
      const res = await refreshStrategy4EventsFMP(fmpKey || null);
      const ts = new Date().toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' });
      setLastFmpSync(ts);
      setStatus({ ok:true, msg:`✓ FMP: ${res.inserted} new events` });
      await load();
    } catch (e) { setStatus({ ok:false, msg:`FMP error: ${e.message}` }); }
    finally { setRefreshing(false); }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setStatus(null);
    try {
      const res = await uploadStrategy4EventsCSV(file);
      setStatus({ ok:true, msg:`✓ CSV: ${res.inserted} events imported${res.error_count ? `, ${res.error_count} errors` : ''}` });
      await load();
    } catch (err) { setStatus({ ok:false, msg:`CSV error: ${err.message}` }); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value=''; }
  };

  // ── Filtering + bucketing ───────────────────────────────────────────────────

  const now   = new Date();
  const today = todayIso();
  const thisYM = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const prevYM = (() => {
    const d = new Date(now.getFullYear(), now.getMonth()-1, 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  })();

  const q = search.toLowerCase();
  let filtered = events;
  if (typeFilter === 'event')  filtered = filtered.filter(e => e.record_type === 'event' || !e.record_type);
  if (typeFilter === 'action') filtered = filtered.filter(e => e.record_type === 'action');
  if (q) filtered = filtered.filter(e =>
    (e.title||'').toLowerCase().includes(q) ||
    (e.category||'').toLowerCase().includes(q) ||
    (e.region||'').toLowerCase().includes(q)
  );

  // For table: hide completed actions unless showCompleted
  const tableEvents = showCompleted
    ? filtered
    : filtered.filter(e => e.record_type !== 'action' || !e.completed_at);

  const pastEvts     = [];
  const mainGroups   = {};
  const futureGroups = {};
  tableEvents.forEach(e => {
    const ym = (e.event_date || '').slice(0, 7);
    if (ym < prevYM)       pastEvts.push(e);
    else if (ym <= thisYM) (mainGroups[ym]  = mainGroups[ym]  || []).push(e);
    else                   (futureGroups[ym] = futureGroups[ym] || []).push(e);
  });

  const futureMonths = Object.entries(futureGroups)
    .sort(([a],[b]) => b.localeCompare(a))
    .map(([ym,evts]) => [ym, [...evts].sort((a,b) => (b.event_date||'').localeCompare(a.event_date||''))]);
  const mainMonths = Object.entries(mainGroups)
    .sort(([a],[b]) => b.localeCompare(a))
    .map(([ym,evts]) => [ym, [...evts].sort((a,b) => (b.event_date||'').localeCompare(a.event_date||''))]);

  const completedCount = filtered.filter(e => e.record_type === 'action' && !!e.completed_at).length;

  // ── Table styles ────────────────────────────────────────────────────────────

  const TH = {
    padding:'6px 10px', fontWeight:700, fontSize:11, color:'var(--ink-3)',
    textAlign:'left', whiteSpace:'nowrap', textTransform:'uppercase', letterSpacing:'.04em',
    borderBottom:'.5px solid var(--hair)', background:'var(--surface-2)',
    position:'sticky', top:0, zIndex:2,
  };

  function renderMonthSep(ym, count) {
    return (
      <tr key={`sep-${ym}`}>
        <td colSpan={8} style={{
          padding:'5px 10px 3px', fontSize:11.5, fontWeight:700,
          color:'var(--ink-3)', background:'var(--surface-2)',
          borderTop:'.5px solid var(--hair)', borderBottom:'.5px solid var(--hair)',
          letterSpacing:'.04em', textTransform:'uppercase', textAlign:'left',
        }}>
          {monthLabel(ym)}
          <span style={{ fontWeight:400, marginLeft:6, opacity:0.65 }}>({count})</span>
        </td>
      </tr>
    );
  }

  function renderEventRow(e) {
    const typeColor = EVENT_TYPE_COLOR[e.event_type] || '#64748b';
    const isManual  = e.source === 'manual' || e.source === 'csv-upload' || e.source === 'csv';
    return (
      <tr key={e.id} style={{ borderBottom:'.5px solid var(--hair)' }}>
        <td style={{ padding:'5px 10px', color:'var(--ink-3)', whiteSpace:'nowrap', fontSize:11.5 }}>{fmtDay(e.event_date)}</td>
        <td style={{ padding:'5px 10px' }}><Tag color={typeColor}>{EVENT_TYPE_LABELS[e.event_type]||e.event_type}{e.category ? ` / ${e.category}` : ''}</Tag></td>
        <td style={{ padding:'5px 10px', fontSize:11.5, color:'var(--ink-3)' }}>{e.region||'—'}</td>
        <td style={{ padding:'5px 10px', fontSize:11, color:'var(--ink)', fontWeight:500, textAlign:'left' }}>{e.title}</td>
        <td style={{ padding:'5px 10px' }}><Tag color={IMPACT_COLOR[e.impact]||'#94a3b8'}>{e.impact}</Tag></td>
        <td style={{ padding:'5px 10px' }}>
          {(() => {
            const src = e.source || 'auto';
            const ss = SOURCE_STYLE[src] || SOURCE_STYLE['auto'];
            return <span style={{ fontSize:11.5, padding:'1px 5px', borderRadius:3, background:ss.bg, color:ss.c }}>{src}</span>;
          })()}
        </td>
        <td style={{ padding:'5px 10px', fontSize:11.5, color:'var(--ink-3)', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {e.notes ? e.notes : <span style={{opacity:0.35}}>—</span>}
        </td>
        <td style={{ padding:'5px 10px', whiteSpace:'nowrap', textAlign:'right' }}>
          {isManual ? (
            <>
              <button onClick={() => handleEdit(e)}
                style={{ fontSize:11.5, padding:'2px 7px', borderRadius:3, cursor:'pointer',
                  background:'var(--surface-3)', border:'.5px solid var(--hair-strong)', color:'var(--ink-3)', marginRight:4 }}>
                Edit
              </button>
              <button onClick={() => handleDelete(e.id, false)}
                style={{ fontSize:11.5, padding:'2px 7px', borderRadius:3, cursor:'pointer',
                  background:'rgba(239,68,68,.08)', border:'.5px solid rgba(239,68,68,.25)', color:'var(--down-field-ink)' }}>
                ✕
              </button>
            </>
          ) : (
            <span style={{ fontSize:11, color:'var(--ink-3)', opacity:0.4 }}>seeded</span>
          )}
        </td>
      </tr>
    );
  }

  function renderActionRow(e) {
    const isCompleted = !!e.completed_at;
    const isOverdue   = !isCompleted && e.event_date < today;
    const catColor    = ACTION_CAT_COLOR[e.category] || '#94a3b8';
    const isLoading   = completingId === e.id;
    return (
      <tr key={e.id} style={{ borderBottom:'.5px solid var(--hair)', opacity: isCompleted ? 0.6 : 1 }}>
        {/* Date + overdue */}
        <td style={{ padding:'5px 10px', whiteSpace:'nowrap', fontSize:11.5, color:'var(--ink-3)' }}>
          <div>{fmtDay(e.event_date)}</div>
          {isOverdue && (
            <span style={{ fontSize:10, fontWeight:700, color:'var(--amber-ink)',
              background:'rgba(245,158,11,.12)', padding:'1px 4px', borderRadius:3,
              display:'inline-block', marginTop:2 }}>OVERDUE</span>
          )}
        </td>
        {/* Type / Category */}
        <td style={{ padding:'5px 10px' }}><Tag color={catColor}>Action / {e.category||'Other'}</Tag></td>
        {/* Region slot — empty for actions */}
        <td style={{ padding:'5px 10px', fontSize:11.5, color:'var(--ink-3)' }}>—</td>
        {/* Title + urgent badge */}
        <td style={{ padding:'5px 10px', fontSize:11, color:'var(--ink)', fontWeight:500, textAlign:'left' }}>
          <span style={{ textDecoration: isCompleted ? 'line-through' : 'none' }}>{e.title}</span>
          {!!e.urgent && !isCompleted && (
            <span style={{ marginLeft:5, fontSize:10, fontWeight:700, color:'var(--amber-ink)',
              background:'rgba(245,158,11,.12)', padding:'1px 4px', borderRadius:3 }}>URGENT</span>
          )}
          {e.event_time && (
            <span style={{ marginLeft:6, fontSize:10, color:'var(--ink-3)' }}>{e.event_time}</span>
          )}
        </td>
        {/* Completion checkbox in impact slot */}
        <td style={{ padding:'5px 10px' }}>
          <button
            disabled={isLoading}
            onClick={() => isCompleted ? handleReopen(e.id) : handleComplete(e.id)}
            style={{ display:'inline-flex', alignItems:'center', gap:5, background:'none', border:'none',
              cursor:'pointer', fontSize:12, color: isCompleted ? 'var(--ink-3)' : catColor,
              opacity: isLoading ? 0.5 : 1 }}>
            <span style={{ fontSize:15 }}>{isCompleted ? '☑' : '☐'}</span>
            <span style={{ fontSize:10.5 }}>{isCompleted ? 'Done' : 'Open'}</span>
          </button>
        </td>
        {/* Source */}
        <td style={{ padding:'5px 10px' }}>
          <span style={{ fontSize:11.5, padding:'1px 5px', borderRadius:3,
            background:'rgba(245,158,11,.10)', color:'var(--amber-ink)' }}>manual</span>
        </td>
        {/* Notes */}
        <td style={{ padding:'5px 10px', fontSize:11.5, color:'var(--ink-3)', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {e.notes ? e.notes : <span style={{opacity:0.35}}>—</span>}
        </td>
        {/* Actions */}
        <td style={{ padding:'5px 10px', whiteSpace:'nowrap', textAlign:'right' }}>
          <button onClick={() => handleEdit(e)}
            style={{ fontSize:11.5, padding:'2px 7px', borderRadius:3, cursor:'pointer',
              background:'var(--surface-3)', border:'.5px solid var(--hair-strong)', color:'var(--ink-3)', marginRight:4 }}>
            Edit
          </button>
          <button onClick={() => handleDelete(e.id, true)}
            style={{ fontSize:11.5, padding:'2px 7px', borderRadius:3, cursor:'pointer',
              background:'rgba(239,68,68,.08)', border:'.5px solid rgba(239,68,68,.25)', color:'var(--down-field-ink)' }}>
            ✕
          </button>
        </td>
      </tr>
    );
  }

  function renderRow(e) {
    return e.record_type === 'action' ? renderActionRow(e) : renderEventRow(e);
  }

  const hasAnyTable = pastEvts.length > 0 || mainMonths.length > 0 || futureMonths.length > 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding:'16px 0', display:'flex', flexDirection:'column' }}>

      {/* Header */}
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:3 }}>
          Events &amp; Actions
        </div>
        <div style={{ fontSize:11, color:'var(--ink-3)' }}>
          Market events and personal to-do actions in one view.
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:10 }}>

        {/* View toggle */}
        {['table','calendar'].map(v => (
          <button key={v}
            className={`pill${viewMode === v ? ' on' : ''}`}
            onClick={() => setViewMode(v)}
            style={{ fontSize:11, textTransform:'capitalize' }}>
            {v === 'table' ? '≡ Table' : '▦ Calendar'}
          </button>
        ))}

        <div style={{ width:1, height:18, background:'var(--hair-strong)', margin:'0 4px' }}/>

        {/* Type filter */}
        {[['all','All'],['event','Events'],['action','Actions']].map(([v,l]) => (
          <button key={v}
            className={`pill${typeFilter === v ? ' on' : ''}`}
            onClick={() => setTypeFilter(v)}
            style={{ fontSize:11 }}>
            {l}
          </button>
        ))}

        <div style={{ width:1, height:18, background:'var(--hair-strong)', margin:'0 4px' }}/>

        {/* Search */}
        <input className="inp" type="text" value={search}
          onChange={e=>setSearch(e.target.value)}
          placeholder="Search title, category…"
          style={{ minWidth:180 }}/>

        {/* Impact filter — only relevant for Events */}
        {typeFilter !== 'action' && ['','HIGH','MED','LOW'].map(v => (
          <button key={v||'all'}
            className={`pill${impactFilter===(v==='MED'?'MEDIUM':v)?' on':''}`}
            onClick={()=>setImpactFilter(f=>f===(v==='MED'?'MEDIUM':v)?'':(v==='MED'?'MEDIUM':v))}
            style={{ fontSize:11 }}>
            {v||'All'}
          </button>
        ))}

        {/* Show completed toggle — only when viewing actions */}
        {typeFilter !== 'event' && completedCount > 0 && (
          <button
            className={`pill${showCompleted ? ' on' : ''}`}
            onClick={() => setShowCompleted(s => !s)}
            style={{ fontSize:11 }}>
            {showCompleted ? `Hide completed (${completedCount})` : `Show completed (${completedCount})`}
          </button>
        )}

        {(search || impactFilter) && (
          <button className="pill" onClick={()=>{setSearch('');setImpactFilter('');}}>Clear</button>
        )}

        {/* Right: add + CSV */}
        <div style={{ display:'flex', gap:6, alignItems:'center', marginLeft:'auto', flexWrap:'wrap' }}>
          {typeFilter !== 'action' && (
            <>
              <label className="pill" style={{ cursor:'pointer', whiteSpace:'nowrap' }}>
                {uploading ? '⏳…' : '↑ CSV'}
                <input ref={fileRef} type="file" accept=".csv" onChange={handleCSVUpload}
                  style={{ display:'none' }} disabled={uploading}/>
              </label>
              <button className="pill" onClick={downloadTemplate}>↓ Template</button>
            </>
          )}

          {/* Add buttons — always rendered, active one becomes ✕ Cancel, other dims */}
          {typeFilter !== 'action' && (
            <button
              onClick={() => setAddType(addType === 'event' ? null : 'event')}
              disabled={addType === 'action'}
              style={{ fontSize:11, padding:'5px 12px', borderRadius:4, cursor: addType === 'action' ? 'default' : 'pointer',
                fontWeight:600, whiteSpace:'nowrap', transition:'all .15s',
                ...(addType === 'event'
                  ? { background:'var(--surface-3)', color:'var(--ink-3)', border:'.5px solid var(--hair-strong)' }
                  : addType === 'action'
                  ? { background:'var(--surface-3)', color:'var(--ink-3)', border:'.5px solid var(--hair)', opacity:0.45 }
                  : { background:'var(--amber-ink)', color:'#fff', border:'none' }
                )}}>
              {addType === 'event' ? '✕ Cancel' : '+ Event'}
            </button>
          )}
          {typeFilter !== 'event' && (
            <button
              onClick={() => setAddType(addType === 'action' ? null : 'action')}
              disabled={addType === 'event'}
              style={{ fontSize:11, padding:'5px 12px', borderRadius:4, cursor: addType === 'event' ? 'default' : 'pointer',
                fontWeight:600, whiteSpace:'nowrap', transition:'all .15s',
                ...(addType === 'action'
                  ? { background:'var(--surface-3)', color:'var(--ink-3)', border:'.5px solid var(--hair-strong)' }
                  : addType === 'event'
                  ? { background:'var(--surface-3)', color:'var(--ink-3)', border:'.5px solid var(--hair)', opacity:0.45 }
                  : { background:'var(--surface-2)', color:'var(--amber-ink)', border:'.5px solid var(--amber-hair,rgba(245,158,11,.4))' }
                )}}>
              {addType === 'action' ? '✕ Cancel' : '+ Action'}
            </button>
          )}
        </div>
      </div>

      {/* FMP refresh — key configured in Settings > Connections */}
      {typeFilter !== 'action' && (
        <div style={{ marginBottom:8, display:'flex', alignItems:'center', gap:8 }}>
          <button className="pill" onClick={handleFMPRefresh} disabled={refreshing}
            style={{ opacity:refreshing?0.6:1, whiteSpace:'nowrap' }}>
            {refreshing
              ? '⏳ Refreshing…'
              : <><IconRefresh size={13} style={{verticalAlign:'middle',marginRight:4}}/> Refresh US Events</>}
          </button>
          {lastFmpSync && <span style={{ fontSize:11, color:'var(--ink-3)' }}>Last: {lastFmpSync}</span>}
        </div>
      )}

      {/* Add / Edit forms — same inline panel */}
      {modalItem ? (
        modalItem.record_type === 'action'
          ? <ActionForm initial={modalItem} onSave={handleModalSave} onCancel={() => setModalItem(null)} saving={savingId === modalItem.id}/>
          : <EventForm  initial={modalItem} onSave={handleModalSave} onCancel={() => setModalItem(null)} saving={savingId === modalItem.id}/>
      ) : addType === 'event' ? (
        <EventForm initial={BLANK_EVENT} onSave={handleAddEvent} onCancel={() => setAddType(null)} saving={savingId === 'new'}/>
      ) : addType === 'action' ? (
        <ActionForm initial={BLANK_ACTION} onSave={handleAddAction} onCancel={() => setAddType(null)} saving={savingId === 'new'}/>
      ) : null}

      {/* Status */}
      {status && (
        <div style={{ marginBottom:10, padding:'6px 10px', borderRadius:4, fontSize:11,
          display:'flex', justifyContent:'space-between', alignItems:'center',
          background: status.ok ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)',
          border: `.5px solid ${status.ok ? 'rgba(34,197,94,.25)' : 'rgba(239,68,68,.25)'}`,
          color: status.ok ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
        }}>
          <span>{status.msg}</span>
          <button onClick={()=>setStatus(null)} style={{ background:'none',border:'none',cursor:'pointer',color:'inherit',fontSize:13 }}>✕</button>
        </div>
      )}

      {/* ── Calendar grid view ── */}
      {viewMode === 'calendar' && (
        <CalendarGrid
          allEvents={filtered}
          typeFilter={typeFilter}
          onComplete={handleComplete}
          onReopen={handleReopen}
          onEdit={handleEdit}
        />
      )}

      {/* ── Table view ── */}
      {viewMode === 'table' && (
        <div style={{ flex:1, overflowY:'auto', maxHeight:'calc(100vh - 300px)', minHeight:200,
          border:'.5px solid var(--hair)', borderRadius:6 }}>
          {loading ? (
            <div style={{ color:'var(--ink-3)', fontSize:11.5, padding:'20px 12px' }}>Loading…</div>
          ) : !hasAnyTable ? (
            <div style={{ color:'var(--ink-3)', fontSize:11.5, padding:'20px 12px' }}>No items match filters.</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
              <thead>
                <tr>
                  <th style={{ ...TH, width:100 }}>Date</th>
                  <th style={{ ...TH, width:130 }}>Type / Category</th>
                  <th style={{ ...TH, width:65 }}>Region</th>
                  <th style={TH}>Title</th>
                  <th style={{ ...TH, width:90 }}>Impact / Status</th>
                  <th style={{ ...TH, width:65 }}>Source</th>
                  <th style={TH}>Notes</th>
                  <th style={{ ...TH, width:90 }}></th>
                </tr>
              </thead>
              <tbody>
                {/* Future */}
                {futureMonths.length > 0 && (
                  <>
                    <tr onClick={() => setShowFuture(s=>!s)} style={{ cursor:'pointer', background:'var(--paper)' }}>
                      <td colSpan={8} style={{
                        padding:'8px 12px', fontSize:11.5, fontWeight:700, textAlign:'left',
                        color:'var(--ink)', letterSpacing:'.04em', textTransform:'uppercase',
                        borderTop:'1.5px solid var(--hair-strong)', borderBottom:'1.5px solid var(--hair-strong)',
                        userSelect:'none',
                      }}>
                        <span style={{ display:'inline-block', marginRight:7, transition:'transform .15s',
                          transform: showFuture ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize:12 }}>▾</span>
                        Future Events &amp; Actions
                        <span style={{ fontWeight:400, color:'var(--ink-3)', marginLeft:6, fontSize:10 }}>
                          ({futureMonths.reduce((n,[,evts])=>n+evts.length,0)})
                        </span>
                      </td>
                    </tr>
                    {showFuture && futureMonths.map(([ym,evts]) => (
                      <React.Fragment key={ym}>
                        {renderMonthSep(ym, evts.length)}
                        {evts.map(e => renderRow(e))}
                      </React.Fragment>
                    ))}
                  </>
                )}

                {/* Current + prev month */}
                {mainMonths.map(([ym,evts]) => (
                  <React.Fragment key={ym}>
                    {renderMonthSep(ym, evts.length)}
                    {evts.map(e => renderRow(e))}
                  </React.Fragment>
                ))}

                {/* Past */}
                {pastEvts.length > 0 && (
                  <>
                    <tr onClick={() => setShowPast(s=>!s)} style={{ cursor:'pointer', background:'var(--paper)' }}>
                      <td colSpan={8} style={{
                        padding:'8px 12px', fontSize:11.5, fontWeight:700, textAlign:'left',
                        color:'var(--ink)', letterSpacing:'.04em', textTransform:'uppercase',
                        borderTop:'1.5px solid var(--hair-strong)', borderBottom:'1.5px solid var(--hair-strong)',
                        userSelect:'none',
                      }}>
                        <span style={{ display:'inline-block', marginRight:7, transition:'transform .15s',
                          transform: showPast ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize:12 }}>▾</span>
                        Past Events &amp; Actions
                        <span style={{ fontWeight:400, color:'var(--ink-3)', marginLeft:6, fontSize:10 }}>
                          ({pastEvts.length})
                        </span>
                      </td>
                    </tr>
                    {showPast && Object.entries(
                      pastEvts.reduce((acc,e) => {
                        const ym = (e.event_date||'').slice(0,7);
                        (acc[ym]=acc[ym]||[]).push(e); return acc;
                      }, {})
                    )
                    .sort(([a],[b])=>b.localeCompare(a))
                    .map(([ym,evts]) => (
                      <React.Fragment key={ym}>
                        {renderMonthSep(ym, evts.length)}
                        {[...evts].sort((a,b)=>(b.event_date||'').localeCompare(a.event_date||'')).map(e => renderRow(e))}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

    </div>
  );
}
